import * as React from 'react'

import { F0WizardForm } from '@factorialco/f0-react'
import type { PayrollConceptSelectFieldFragment } from 'generated/resources/graphql'
import { uniqueId } from 'lodash'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { useCategorizedPayrollConcepts } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'
import { mapAmountAndUpperLimitToCents } from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationForm/AdditionalCompensationFormUtils'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { parseDateString } from 'modules/contracts/lib/utils'

import { useCreateAdditionalCompensationWizardForm } from './useCreateAdditionalCompensationWizardForm'
import {
  type BasicContext,
  type BasicStepValues,
  FIXED_AMOUNT_STRATEGY,
  type RawDefinitionStepValues,
  isBasicStepValues,
  isRawDefinitionStepValues,
  isSupportedAmountStrategyType,
} from './lib/createAdditionalCompensationWizardModal.shared'
import {
  buildAdditionalCompensation,
  createBasicContextFromValues,
  getSelectedPayrollConcept,
  normalizeDefinitionValues,
} from './lib/createAdditionalCompensationWizardModal.utils'
import { getFirstPaymentDate } from './lib/getFirstPaymentDate'

type CreateAdditionalCompensationWizardModalProps = {
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  onClose?: () => void
  effectiveOn?: string
  contractVersionId: string
}

const initialBasicContext: BasicContext = {
  amountStrategyType: FIXED_AMOUNT_STRATEGY,
}

function CreateAdditionalCompensationWizardModal({
  onClose,
  payrollConcepts,
  effectiveOn,
  contractVersionId,
}: CreateAdditionalCompensationWizardModalProps) {
  const { additionalCompensations, setAdditionalCompensations } = useContractFlow()
  const [basicContext, setBasicContext] = React.useState<BasicContext>(initialBasicContext)
  const payrollConceptOptions = useCategorizedPayrollConcepts(payrollConcepts)
  const selectedPayrollConcept = React.useMemo(
    () =>
      getSelectedPayrollConcept({
        payrollConcepts,
        payrollConceptId: basicContext.payrollConceptId,
      }),
    [basicContext.payrollConceptId, payrollConcepts]
  )
  const basicValuesRef = React.useRef<BasicStepValues | null>(null)

  const defaultFirstPaymentDate = React.useMemo(() => {
    const parsedFirstPaymentDate = parseDateString(getFirstPaymentDate(effectiveOn))
    return parsedFirstPaymentDate ? parsedFirstPaymentDate.toDate() : undefined
  }, [effectiveOn])

  const handleSubmit = React.useCallback(
    async ({
      sectionId,
      data,
    }: {
      sectionId: 'basic' | 'definition'
      data: BasicStepValues | RawDefinitionStepValues
    }) => {
      if (sectionId === 'basic') {
        if (!isBasicStepValues(data)) {
          return { success: false }
        }

        const basicValues = data

        basicValuesRef.current = basicValues
        setBasicContext((current) => {
          const nextContext = createBasicContextFromValues(basicValues)

          if (
            current.amountStrategyType === nextContext.amountStrategyType &&
            current.payrollConceptId === nextContext.payrollConceptId
          ) {
            return current
          }

          return nextContext
        })

        return { success: true }
      }

      const basicValues = basicValuesRef.current

      if (!basicValues) {
        return { success: false }
      }

      if (!isSupportedAmountStrategyType(basicValues.amountStrategyType)) {
        return { success: false }
      }

      if (!isRawDefinitionStepValues(data)) {
        return { success: false }
      }

      const additionalCompensation = buildAdditionalCompensation({
        basicValues,
        definitionValues: normalizeDefinitionValues(data),
        contractVersionId,
        payrollConcepts,
        createTemporaryId: () => uniqueId(),
      })

      const mappedValues = mapAmountAndUpperLimitToCents(additionalCompensation)

      setAdditionalCompensations([...(additionalCompensations ?? []), mappedValues])

      track(trackingEvents.compensations.createCompensation, {
        type: 'contract',
      })

      onClose?.()

      return { success: true }
    },
    [
      additionalCompensations,
      contractVersionId,
      onClose,
      payrollConcepts,
      setAdditionalCompensations,
    ]
  )

  const { formDefinition, steps } = useCreateAdditionalCompensationWizardForm({
    payrollConcepts,
    payrollConceptOptions,
    effectiveOn,
    defaultFirstPaymentDate,
    basicContext,
    selectedPayrollConceptUnitType: selectedPayrollConcept?.unitType,
    onSubmit: handleSubmit,
  })

  return (
    <F0WizardForm
      isOpen
      onClose={onClose}
      title={i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_form_modal.title'
      )}
      formDefinition={formDefinition}
      steps={steps}
      nextLabel={i18n.t('common.continue')}
      previousLabel={i18n.t('common.back')}
      width="xl"
    />
  )
}

export { CreateAdditionalCompensationWizardModal }
